import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { DashboardSetting } from '@Apis/companyDashboard';
import { TASK_FILTER_ID_FIELDS, LOCATION_FILTER_ID_FIELDS } from '../filterFields';
import './ViewsPanel.scss';

interface ViewsPanelProps {
    settings: DashboardSetting[];
    selectedId: string;
    onSelect: (id: string) => void;
    onSaveCurrent: () => void;
    onDelete: (id: string) => void;
}

// Same task/location split OverviewFilterControls.tsx uses for its own active-count badge, so a
// view's "N filters · Locations" category here always agrees with which of the two filter
// dialogs (Task Filters / Location Filters) its filters actually live in.
const TASK_ID_FIELDS: Array<keyof DashboardSetting> = [...TASK_FILTER_ID_FIELDS];
const LOCATION_ID_FIELDS: Array<keyof DashboardSetting> = [...LOCATION_FILTER_ID_FIELDS];

const countActive = (setting: DashboardSetting, fields: Array<keyof DashboardSetting>): number =>
    fields.reduce((count, field) => {
        const value = setting[field];
        return Array.isArray(value) && value.length > 0 ? count + 1 : count;
    }, 0);

const describeView = (setting: DashboardSetting): string => {
    const taskCount = countActive(setting, TASK_ID_FIELDS) + (setting.isMilestonesOnly === true ? 1 : 0);
    const locationCount = countActive(setting, LOCATION_ID_FIELDS);
    const total = taskCount + locationCount;

    if (total === 0) return 'No filters';

    const noun = total === 1 ? 'filter' : 'filters';
    const category = taskCount > 0 && locationCount > 0
        ? 'Tasks & Locations'
        : taskCount > 0 ? 'Tasks' : 'Locations';

    return `${total} ${noun} · ${category}`;
};

const ViewsPanel = ({ settings, selectedId, onSelect, onSaveCurrent, onDelete }: ViewsPanelProps): ReactElement => {
    const [isOpen, setIsOpen] = useState(false);
    // Armed by a first click on a row's trash icon; a second click on the same row confirms the
    // delete. Cleared whenever the dropdown closes so a stale arm can't survive to the next open.
    const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
    const containerRef = useRef<HTMLDivElement>(null);

    // Closes on any click outside the trigger/panel — there's no backdrop, so nothing else does.
    useEffect(() => {
        if (!isOpen) return;
        const onClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', onClickOutside);
        return () => document.removeEventListener('mousedown', onClickOutside);
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) setConfirmDeleteId(null);
    }, [isOpen]);

    // Real saved views only — the synthetic "Unsaved View"/default entry has no id and isn't
    // something the user saved, so it doesn't belong in the list or the count badge.
    const savedViews = settings.filter((setting) => !!setting.id);

    return (
        <div className='views-panel' ref={containerRef}>
            <button type='button' className='btn views-panel-trigger' onClick={() => setIsOpen((open) => !open)}>
                <i className='fa-regular fa-bookmark'></i>
                <span className='ms-2'>Views</span>
                {savedViews.length > 0 && <span className='views-panel-badge'>{savedViews.length}</span>}
                <i className={`fa-regular fa-chevron-${isOpen ? 'up' : 'down'} ms-2`}></i>
            </button>

            {isOpen && (
                <div className='views-panel-dropdown'>
                    <div className='views-panel-header'>
                        <span>Saved Views</span>
                        <button
                            type='button'
                            className='views-panel-save-current'
                            onClick={() => {
                                onSaveCurrent();
                                setIsOpen(false);
                            }}>
                            <i className='fa-kit fa-regular-bookmark-circle-plus'></i>
                            <span className='ms-1'>Save current</span>
                        </button>
                    </div>

                    {savedViews.length === 0 ? (
                        <div className='views-panel-empty'>No saved views yet</div>
                    ) : (
                        <ul className='views-panel-list'>
                            {savedViews.map((setting) => (
                                <li
                                    key={setting.id}
                                    role='button'
                                    tabIndex={0}
                                    className={`views-panel-item ${setting.id === selectedId ? 'active' : ''}`}
                                    onClick={() => {
                                        onSelect(setting.id as string);
                                        setIsOpen(false);
                                    }}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            onSelect(setting.id as string);
                                            setIsOpen(false);
                                        }
                                    }}>
                                    <div className='views-panel-item-text'>
                                        <div className='views-panel-item-name'>{setting.name ?? 'Untitled view'}</div>
                                        <div className='views-panel-item-meta'>
                                            {confirmDeleteId === setting.id ? 'Click again to delete' : describeView(setting)}
                                        </div>
                                    </div>
                                    <button
                                        type='button'
                                        className={`views-panel-item-delete ${confirmDeleteId === setting.id ? 'confirming' : ''}`}
                                        aria-label={confirmDeleteId === setting.id ? `Confirm delete ${setting.name ?? 'Untitled view'}` : `Delete ${setting.name ?? 'Untitled view'}`}
                                        onClick={(event) => {
                                            event.stopPropagation();
                                            if (confirmDeleteId === setting.id) {
                                                onDelete(setting.id as string);
                                                setConfirmDeleteId(null);
                                            } else {
                                                setConfirmDeleteId(setting.id as string);
                                            }
                                        }}>
                                        <i className={`fa-regular ${confirmDeleteId === setting.id ? 'fa-check' : 'fa-trash-can'}`}></i>
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default ViewsPanel;
